import { uploadImage } from "@/api/image";
import { useUpdateMultipleSample } from "@/api/multiplesample";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Multiplesample } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import RichTextField from "../form-fields/RichTextField";
import TextField from "../form-fields/TextField";
import StyledDrawer from "../StyledDrawer";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";

const schema = z.object({
  exemplaireDansEstampesMultiplesDescriptionNumero: z.string().optional(),
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire: z
    .string()
    .optional(),
  exemplaireDansEstampesMultiplesDescriptionLocalisation: z.string().optional(),
  exemplaireDansEstampesMultiplesDescriptionNotes: z.string().optional(),
  file: z.instanceof(File).optional(),
});

type SchemaType = z.infer<typeof schema>;

const EditExemplaireModal = ({
  multiplesample,
}: {
  multiplesample: Multiplesample;
}) => {
  const [open, setOpen] = useState(false);
  const { instanceData } = useAuth();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      exemplaireDansEstampesMultiplesDescriptionNumero:
        multiplesample.exemplaireDansEstampesMultiplesDescriptionNumero,
      exemplaireDansEstampesMultiplesDescriptionNumeroInventaire:
        multiplesample.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
      exemplaireDansEstampesMultiplesDescriptionLocalisation:
        multiplesample.exemplaireDansEstampesMultiplesDescriptionLocalisation,
      exemplaireDansEstampesMultiplesDescriptionNotes:
        multiplesample.exemplaireDansEstampesMultiplesDescriptionNotes,
    },
  });

  const { t } = useTranslation();

  const { control, getValues, setValue, trigger } = form;

  const { mutateAsync: updateMultiplesample, isPending } =
    useUpdateMultipleSample();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setValue("file", acceptedFiles[0]);
        trigger();
      }
    },
    [setValue, getValues, trigger],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const onSubmit = async (data: SchemaType) => {
    if (multiplesample.id) {
      if (instanceData == null) {
        console.error("Tried to upload a Document, but instanceData is null");
        return;
      }
      try {
        await updateMultiplesample({
          data: {
            exemplaireDansEstampesMultiplesDescriptionNumero:
              data.exemplaireDansEstampesMultiplesDescriptionNumero || "",
            exemplaireDansEstampesMultiplesDescriptionNumeroInventaire:
              data.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire ||
              "",
            exemplaireDansEstampesMultiplesDescriptionLocalisation:
              data.exemplaireDansEstampesMultiplesDescriptionLocalisation || "",
            exemplaireDansEstampesMultiplesDescriptionNotes:
              data.exemplaireDansEstampesMultiplesDescriptionNotes || "",
          },
          where: { id: multiplesample.id },
        });

        if (data.file) {
          await uploadImage(
            data.file,
            {
              name: "document_" + multiplesample.id + "_examplaires",
              position:
                "exemplairedansestampesmultiplesdescriptionphotoexemplaire",
              docId: multiplesample.id,
              details: "",
              published: false,
            },
            instanceData.s3Bucket,
            instanceData.id,
          );
        }

        form.reset();
        toast({
          title: t("successEditExemplaire"),
          variant: "success",
        });
        setOpen(false);
      } catch (error) {
        toast({
          title: t("errorEditExemplaire"),
          variant: "destructive",
        });
        console.error(error);
      }
    }
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      form.reset();
    }
  };

  return (
    <StyledDrawer
      sheetProps={{
        open: open,
        onOpenChange: onOpenChange,
      }}
      title={t("editExemplaire")}
      className="flex flex-col justify-between overflow-y-auto"
      trigger={
        <Button key="edit" size="icon" type="button" variant="ghost">
          <Pencil className="w-5 h-5 text-gray-500" />
        </Button>
      }
      footer={
        <CustomDialogFooter>
          <Button
            className="w-full"
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            {t("formCancel")}
          </Button>
          <Button
            className="w-full"
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            disabled={!form.formState.isValid}
          >
            {isPending ? <Loader2 className="w-4 h-4 mr-2" /> : null}
            {t("save")}
          </Button>
        </CustomDialogFooter>
      }
    >
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <TextField
            config={{
              name: "exemplaireDansEstampesMultiplesDescriptionNumero",
              type: "text",
              label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
            }}
            control={control}
            getValues={getValues}
          />
          <TextField
            config={{
              name: "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
              type: "text",
              label: t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
              ),
            }}
            control={control}
            getValues={getValues}
          />
          <TextField
            config={{
              name: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
              type: "text",
              label: t(
                "exemplaireDansEstampesMultiplesDescriptionLocalisation",
              ),
            }}
            control={control}
            getValues={getValues}
          />
          <RichTextField
            config={{
              name: "exemplaireDansEstampesMultiplesDescriptionNotes",
              type: "text",
              label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
            }}
            control={control}
            getValues={getValues}
          />
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Déposez le fichier ici...</p>
            ) : (
              <div>
                <p>{t("uploadChooseFileOrDropFileHerePlaceholder")}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {getValues("file") &&
                    `${t("uploadSaveFile")} : ${getValues("file")?.name}`}
                </p>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </StyledDrawer>
  );
};

export default EditExemplaireModal;
